import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Tab, Tabs } from "@mui/material";

import { MainLeftTitle } from "../../../_uiComponents/WrapperComponents";
import CxAddWinnerManualForm from "./views/CxAddWinnerManualForm";
import CxAddWinnersImport from "./views/CxAddWinnersImport";
import CxAddWinnersResults from "./views/CxAddWinnersResults";

import { cxAlwaysInTouchConstants } from "../../../../_redux/_constants/cxAlwaysInTouch.constants";
import {
     cxAddWinnersRequest,
     cxClearAddWinnersResult,
     cxGetJobRolesRequest,
     retailSalesDealerLookupRequest,
} from "../../../../_redux/_actions";

export default function CxAddWinnersWrapper() {
     const actionDispatch = useDispatch();
     const navigate = useNavigate();

     // The dealer picker is a plain read-only lookup over every active Equipe dealer. It is
     // shared rather than duplicated for CX: it is not retail data, it is the whole network, and a
     // second copy would drift from this one.
     const dealerLookupList = useSelector(
          (state) => state?.retailSales?.dealerLookupList,
     );
     // Every active job role, whole and unpaged, so a winner can be given any role that already
     // exists. The retail filter options carry only that campaign's four roles, which the CX
     // handler rejects.
     const jobRoleList = useSelector((state) => state?.cxAlwaysInTouch?.jobRoles);
     const addWinnersResult = useSelector(
          (state) => state?.cxAlwaysInTouch?.addWinnersResult,
     );

     // True from the moment the batch is sent until the result or an error comes back, so the add
     // button cannot be pressed a second time and pay every winner twice.
     const isSubmitting = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               cxAlwaysInTouchConstants.CX_ADD_WINNERS_REQUEST,
          ),
     );

     const [selectedTab, setSelectedTab] = useState(0);
     const [dealerSearch, setDealerSearch] = useState("");
     const [submittedRows, setSubmittedRows] = useState([]);

     useEffect(() => {
          actionDispatch(cxGetJobRolesRequest());
     }, [actionDispatch]);

     // The dealer picker is searchable and the backend matches what was typed.
     useEffect(() => {
          const timer = setTimeout(() => {
               actionDispatch(
                    retailSalesDealerLookupRequest({
                         search: dealerSearch || undefined,
                    }),
               );
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, dealerSearch]);

     useEffect(() => {
          return () => {
               actionDispatch(cxClearAddWinnersResult());
          };
     }, [actionDispatch]);

     const dealerOptions = useMemo(
          () =>
               (dealerLookupList || []).map((dealer) => ({
                    id: dealer.id,
                    dealerCode: dealer.dealerCode,
                    name: dealer.name,
                    label: `${dealer.dealerCode} - ${dealer.name}`,
               })),
          [dealerLookupList],
     );

     const jobRoleOptions = jobRoleList || [];

     const submitWinners = useCallback(
          (winners) => {
               setSubmittedRows(winners);
               actionDispatch(cxAddWinnersRequest({ winners }));
          },
          [actionDispatch],
     );

     const done = useCallback(() => {
          actionDispatch(cxClearAddWinnersResult());
          navigate("/app/cx-always-in-touch-winners");
     }, [actionDispatch, navigate]);

     return (
          <>
               <MainLeftTitle title="Add CX Always in Touch Winners" />

               <div className="sba-telerik-components-wr">
                    {/* Either the entry form or the outcome is on screen, never both. */}
                    {addWinnersResult ? (
                         <CxAddWinnersResults
                              result={addWinnersResult}
                              rows={submittedRows}
                              onDone={done}
                         />
                    ) : (
                         <>
                              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                   <Tabs
                                        value={selectedTab}
                                        onChange={(event, value) => {
                                             setDealerSearch("");
                                             setSelectedTab(value);
                                        }}
                                   >
                                        <Tab label="Add one winner" />
                                        <Tab label="Import monthly list" />
                                   </Tabs>
                              </Box>

                              <Box sx={{ pt: 3 }}>
                                   {selectedTab === 0 && (
                                        <CxAddWinnerManualForm
                                             dealerOptions={dealerOptions}
                                             jobRoleOptions={jobRoleOptions}
                                             isSubmitting={isSubmitting}
                                             onDealerSearch={setDealerSearch}
                                             onSubmit={submitWinners}
                                        />
                                   )}
                                   {selectedTab === 1 && (
                                        <CxAddWinnersImport
                                             dealerOptions={dealerOptions}
                                             jobRoleOptions={jobRoleOptions}
                                             isSubmitting={isSubmitting}
                                             onSubmit={submitWinners}
                                        />
                                   )}
                              </Box>
                         </>
                    )}
               </div>
          </>
     );
}
